import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { fileUrl, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ChevronLeft,
  Lock,
  Check,
  Sparkles,
  User as UserIcon,
  Frame,
  Crown,
  Gem,
} from "lucide-react";
import GemBadge from "../components/GemBadge";

const SLOT_META = {
  frame:       { label: "Profile Frame",   description: "A glowing border around your avatar.",       Icon: Frame },
  title:       { label: "Profile Title",   description: "A status badge next to your name.",          Icon: Crown },
  gem_variant: { label: "Gem Variant",     description: "Alternate look for your tier gem.",          Icon: Gem },
};

const TRACK_LABEL = { customer: "Discovery", vendor: "Business", both: "Universal" };

function CosmeticCard({ item, onEquip, onUnequip, busy, tierKey }) {
  const isFrame = item.slot === "frame";
  const isGem = item.slot === "gem_variant";
  const locked = !item.unlocked;
  // Build the variant payload the same shape as `/api/reputation/bulk` returns
  // so the card preview uses the EXACT same renderer as everywhere else.
  const gemVariantPayload = isGem
    ? {
        id: item.id,
        label: item.label,
        color: item.color,
        render: item.render || (item.icon ? "food" : "tier"),
        tier_alias: item.tier_alias,
        icon: item.icon,
        anim_class: item.anim_class,
        tag: item.tag,
      }
    : null;
  return (
    <div
      className={`relative rounded-2xl p-3 border transition-colors ${
        item.equipped
          ? "border-pink-400/50 bg-pink-500/10 shadow-[0_0_24px_-12px_rgba(236,72,153,0.7)]"
          : locked
          ? "border-white/10 bg-white/[0.02] opacity-90"
          : "border-white/15 bg-white/[0.05] hover:border-pink-400/30"
      }`}
      data-testid={`cosmetic-card-${item.id}`}
    >
      {item.equipped && (
        <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-black bg-pink-500 text-white shadow-md" data-testid={`cosmetic-equipped-${item.id}`}>
          Equipped
        </span>
      )}
      <div className="flex items-center gap-3">
        {/* Preview */}
        <div className="w-14 h-14 rounded-full bg-zinc-900 border border-white/10 grid place-items-center shrink-0 overflow-hidden relative">
          {isFrame ? (
            <div className={`hg-frame ${!locked ? item.css_class || "" : ""} w-12 h-12 grid place-items-center`}>
              <div className="w-11 h-11 rounded-full bg-zinc-800 grid place-items-center">
                <UserIcon className="w-5 h-5 text-zinc-400" />
              </div>
            </div>
          ) : isGem ? (
            <div className="w-12 h-12 grid place-items-center" data-testid={`cosmetic-preview-gem-${item.id}`}>
              <GemBadge tier={tierKey || "white"} size="xl" variant={gemVariantPayload} />
            </div>
          ) : (
            <span
              className="text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border text-center leading-tight max-w-[48px]"
              style={{ color: item.color, borderColor: `${item.color}66`, background: `${item.color}1a` }}
            >
              {item.label.split(" ")[0]}
            </span>
          )}
          {locked && (
            <div className="absolute inset-0 bg-black/55 grid place-items-center backdrop-blur-[1px]">
              <Lock className="w-4 h-4 text-zinc-300" />
            </div>
          )}
        </div>
        {/* Meta */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black tracking-tight truncate" style={{ color: locked ? "#A1A1AA" : item.color }}>
            {item.label}
          </p>
          <p className="text-[11px] text-zinc-400 truncate">{item.description}</p>
          {locked ? (
            <div className="mt-1.5">
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-pink-400 to-cyan-400"
                  style={{ width: `${item.progress.percent}%` }}
                />
              </div>
              <p className="text-[10px] text-zinc-500 mt-1 font-bold uppercase tracking-widest">
                {item.progress.current}/{item.progress.required} · {progressHint(item.criteria)}
              </p>
            </div>
          ) : (
            <div className="mt-2 flex gap-1.5">
              {item.equipped ? (
                <Button
                  size="sm"
                  onClick={() => onUnequip(item.slot)}
                  disabled={busy}
                  className="h-7 px-3 text-[10px] uppercase tracking-widest font-bold rounded-full bg-white/5 hover:bg-white/10 text-white border border-white/15"
                  data-testid={`cosmetic-unequip-${item.id}`}
                >
                  Unequip
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => onEquip(item.id)}
                  disabled={busy}
                  className="h-7 px-3 text-[10px] uppercase tracking-widest font-bold rounded-full bg-pink-500 hover:bg-pink-400 text-white"
                  data-testid={`cosmetic-equip-${item.id}`}
                >
                  <Check className="w-3 h-3 mr-1" /> Equip
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function progressHint(criteria) {
  const map = {
    min_score:             "reputation pts",
    min_completed_orders:  "completed orders",
    min_reviews_authored:  "reviews left",
    min_followers:         "followers",
    min_repeat_customers:  "repeat buyers",
    min_completed_sales:   "completed sales",
    min_positive_reviews:  "good reviews",
    min_likes_received:    "post likes",
  };
  return map[criteria?.type] || criteria?.type;
}

export default function ProfileCustomize() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeSlot, setActiveSlot] = useState("frame");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/cosmetics/catalog");
      setData(d);
      if (d.newly_unlocked?.length) {
        toast.success(`🎉 ${d.newly_unlocked.length} new unlock${d.newly_unlocked.length > 1 ? "s" : ""}!`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load cosmetics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    load();
  }, [authLoading, user, navigate, load]);

  const equip = async (id) => {
    setBusy(true);
    try {
      await api.post("/cosmetics/equip", { cosmetic_id: id });
      toast.success("Equipped");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't equip");
    } finally {
      setBusy(false);
    }
  };

  const unequip = async (slot) => {
    setBusy(true);
    try {
      await api.post("/cosmetics/unequip", { slot });
      toast.success("Unequipped");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't unequip");
    } finally {
      setBusy(false);
    }
  };

  const slots = useMemo(() => {
    if (!data) return { frame: [], title: [], gem_variant: [] };
    const out = { frame: [], title: [], gem_variant: [] };
    for (const item of data.catalog) {
      (out[item.slot] || (out[item.slot] = [])).push(item);
    }
    return out;
  }, [data]);

  const equipped = data?.equipped || {};
  const equippedFrame = data?.catalog?.find((i) => i.id === equipped.frame);
  const equippedTitle = data?.catalog?.find((i) => i.id === equipped.title);
  const equippedGem   = data?.catalog?.find((i) => i.id === equipped.gem_variant);
  const equippedGemPayload = equippedGem
    ? {
        id: equippedGem.id,
        label: equippedGem.label,
        color: equippedGem.color,
        render: equippedGem.render || (equippedGem.icon ? "food" : "tier"),
        tier_alias: equippedGem.tier_alias,
        icon: equippedGem.icon,
        anim_class: equippedGem.anim_class,
        tag: equippedGem.tag,
      }
    : null;
  const tierKey = data?.stats?.tier_key || "white";
  const avatarUrl = user?.customer_profile?.avatar_url ? fileUrl(user.customer_profile.avatar_url) : user?.picture;

  const stats = data?.stats || {};
  const unlockedCount = (data?.catalog || []).filter((i) => i.unlocked).length;
  const totalCount = (data?.catalog || []).length;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-5 pb-24" data-testid="customize-page">
      <button
        onClick={() => navigate("/profile")}
        className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white"
        data-testid="customize-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back to profile
      </button>

      {/* Live Preview */}
      <div className="rounded-3xl p-5 bg-gradient-to-br from-pink-500/15 via-cyan-500/10 to-transparent border border-pink-400/25" data-testid="customize-preview">
        <p className="text-[10px] uppercase tracking-widest text-pink-300 font-black mb-3 inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Live preview
        </p>
        <div className="flex items-center gap-4">
          <div className={`hg-frame ${equippedFrame?.css_class || ""} w-24 h-24 grid place-items-center shrink-0`}>
            <div className="w-[88px] h-[88px] rounded-full overflow-hidden bg-zinc-800 grid place-items-center border border-white/10">
              {avatarUrl ? (
                <img src={avatarUrl} alt={user?.name} className="w-full h-full object-cover" />
              ) : (
                <UserIcon className="w-10 h-10 text-zinc-400" />
              )}
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-black tracking-tighter truncate">{user?.name}</h2>
              <GemBadge tier={tierKey} size="md" variant={equippedGemPayload} />
              {equippedTitle && (
                <span
                  className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-black border"
                  style={{ color: equippedTitle.color, borderColor: `${equippedTitle.color}66`, background: `${equippedTitle.color}1a` }}
                  data-testid="customize-preview-title"
                >
                  {equippedTitle.label}
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-400 mt-1">
              {unlockedCount}/{totalCount} unlocked · {stats.score || 0} reputation
            </p>
            {equippedGem && (
              <p className="text-[11px] text-cyan-300 font-bold mt-1" data-testid="customize-preview-gem-label">
                Gem skin: {equippedGem.label}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Slot picker tabs */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="customize-slot-tabs">
        {["frame", "title", "gem_variant"].map((slot) => {
          const meta = SLOT_META[slot];
          const Icon = meta.Icon;
          const count = (slots[slot] || []).filter((i) => i.unlocked).length;
          const total = (slots[slot] || []).length;
          const active = activeSlot === slot;
          return (
            <button
              key={slot}
              onClick={() => setActiveSlot(slot)}
              className={`shrink-0 rounded-full h-10 px-4 text-xs font-bold uppercase tracking-widest border transition-colors flex items-center gap-1.5 ${
                active
                  ? "bg-pink-500/20 text-pink-200 border-pink-400/40"
                  : "bg-white/5 text-zinc-300 border-white/10 hover:bg-white/10"
              }`}
              data-testid={`customize-slot-${slot}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {meta.label}
              <span className="ml-1 text-[10px] text-zinc-400 font-bold">{count}/{total}</span>
            </button>
          );
        })}
      </div>

      {/* Slot meta */}
      <p className="text-xs text-zinc-400 -mt-2">{SLOT_META[activeSlot]?.description}</p>

      {/* Cards */}
      {loading ? (
        <p className="text-center text-zinc-500 py-10">Loading cosmetics…</p>
      ) : (
        <div className="space-y-2.5" data-testid={`customize-list-${activeSlot}`}>
          {activeSlot === "title" && (
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">
              {user?.is_vendor ? "Customer + Vendor tracks" : "Customer track · become a vendor to unlock business titles"}
            </p>
          )}
          {(slots[activeSlot] || []).map((item) => (
            <CosmeticCard
              key={item.id}
              item={item}
              onEquip={equip}
              onUnequip={unequip}
              busy={busy}
              tierKey={tierKey}
            />
          ))}
          {(slots[activeSlot] || []).length === 0 && (
            <p className="text-center text-zinc-500 py-6 text-sm">Nothing here yet.</p>
          )}
        </div>
      )}

      {activeSlot === "title" && (
        <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/[0.06] p-3" data-testid="customize-track-hint">
          <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-1 inline-flex items-center gap-1">
            <Crown className="w-3 h-3" /> Two tracks, one identity
          </p>
          <p className="text-xs text-zinc-200">
            Customer titles reward exploring the city — order, review, follow. Vendor titles reward running a great shop — followers, repeat customers, great sales.
          </p>
        </div>
      )}
    </div>
  );
}
